import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { useLang } from '@/i18n/LanguageContext';
import { API } from '@/lib/config';
import { Container, SectionKicker, SectionHeading, TricolourRibbon } from '@/components/common/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Sparkles, MessageSquareQuote, CheckCircle2, ArrowRight } from 'lucide-react';
import { NAMASKAR } from '@/data/namaskarContent';

export default function DialoguePage() {
  const { t } = useLang();
  const N = NAMASKAR;

  const [form, setForm] = useState({
    respondent_name: '',
    email: '',
    state: '',
    district: '',
    profession: '',
    reform_1: '',
    reform_2: '',
    reform_3: '',
    reform_4: '',
    reform_5: '',
    additional_thoughts: '',
    consent_share: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const update = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e && e.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSuccess(null);
    try {
      const payload = { ...form };
      // strip empty optional strings so backend doesn't try to validate them as EmailStr etc
      ['respondent_name', 'email', 'state', 'district', 'profession', 'additional_thoughts'].forEach((k) => {
        if (typeof payload[k] === 'string' && payload[k].trim() === '') delete payload[k];
      });
      const res = await axios.post(`${API}/reforms`, payload);
      const msg = res.data.message || 'Thank you for contributing to the NGRP Governance Reform Dialogue.';
      toast.success(msg);
      setSuccess({ id: res.data.id, message: msg });
      setForm({
        respondent_name: '', email: '', state: '', district: '', profession: '',
        reform_1: '', reform_2: '', reform_3: '', reform_4: '', reform_5: '',
        additional_thoughts: '', consent_share: false,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.slice(-1)[0] || 'field'}: ${d.msg}`).join(', ')
        : (typeof detail === 'string' ? detail : 'Please review your responses and try again.');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* Namaskar hero */}
      <section className="hero-surface">
        <Container className="py-14 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              <SectionKicker>Founder&apos;s Invitation</SectionKicker>
              <h1 className="font-display mt-3 text-4xl sm:text-5xl font-semibold text-[#062B5C] tracking-[-0.02em]">
                {N.greeting}
              </h1>
              <div className="mt-6 space-y-4 max-w-2xl text-[#0B1220] leading-relaxed text-base sm:text-[17px]">
                {N.paragraphs.map((p, i) => (<p key={i}>{p}</p>))}
              </div>
              <div className="mt-6 rounded-lg bg-white border border-[#D7E1EF] p-5 max-w-2xl">
                <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">Our philosophy</div>
                <div className="mt-2 font-display text-lg sm:text-xl font-semibold text-[#0B1220]">
                  {N.philosophy}
                </div>
              </div>
              <div className="mt-6 space-y-2 max-w-2xl text-[#0B1220] leading-relaxed">
                <p>{N.invited_line}</p>
                <div className="flex flex-wrap gap-2 pt-2">
                  {N.invited_groups.map((g) => (
                    <span key={g} className="inline-flex items-center rounded-full border border-[#D7E1EF] bg-white px-3 py-1.5 text-xs font-medium text-[#062B5C]">
                      {g}
                    </span>
                  ))}
                </div>
              </div>
              <div className="mt-6 max-w-2xl">
                <div className="text-[#5B6B82] leading-relaxed">
                  {N.disclaimer.map((d, i) => (<p key={i}>{d}</p>))}
                </div>
              </div>
            </div>

            <aside className="lg:col-span-4">
              <Card className="border-[#D7E1EF] bg-[#062B5C] text-white sticky top-24">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-white/80">
                    <MessageSquareQuote className="h-3.5 w-3.5" />
                    {N.first_ask.kicker}
                  </div>
                  <div className="mt-3 font-display text-xl sm:text-2xl font-semibold text-white leading-snug">
                    {N.first_ask.question}
                  </div>
                  <p className="mt-3 text-sm text-white/85">{N.first_ask.cta}</p>
                  <p className="mt-6 text-sm text-white/85 border-t border-white/15 pt-4">{N.sequence}</p>
                  <div className="mt-6 space-y-1 text-white/80 text-xs">
                    {N.signature_lines.map((s, i) => (<div key={i}>{s}</div>))}
                  </div>
                </CardContent>
              </Card>
            </aside>
          </div>
        </Container>
      </section>

      {/* Reform submission form */}
      <section className="py-16 bg-white border-y border-[#D7E1EF]">
        <Container>
          <SectionHeading
            eyebrow="Governance Reform Dialogue"
            title="Share the five biggest reforms India needs"
            subtitle="Every response feeds directly into the NGRP policy agenda. Speak plainly. Be specific. Your five ideas matter."
          />

          <form
            onSubmit={onSubmit}
            data-testid="dialogue-form"
            className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8"
          >
            {/* Respondent (optional) */}
            <div className="lg:col-span-4 space-y-5">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">About you (optional)</div>
                <p className="mt-1 text-xs text-[#5B6B82]">Optional — leave blank to remain anonymous.</p>
              </div>
              <Field id="dl-name" label="Full Name">
                <Input id="dl-name" value={form.respondent_name} onChange={update('respondent_name')} data-testid="dialogue-name" />
              </Field>
              <Field id="dl-email" label="Email">
                <Input id="dl-email" type="email" value={form.email} onChange={update('email')} data-testid="dialogue-email" />
              </Field>
              <Field id="dl-state" label="State">
                <Input id="dl-state" value={form.state} onChange={update('state')} data-testid="dialogue-state" />
              </Field>
              <Field id="dl-district" label="District">
                <Input id="dl-district" value={form.district} onChange={update('district')} data-testid="dialogue-district" />
              </Field>
              <Field id="dl-prof" label="Profession">
                <Input id="dl-prof" value={form.profession} onChange={update('profession')} data-testid="dialogue-profession" />
              </Field>
            </div>

            {/* The five reforms */}
            <div className="lg:col-span-8 space-y-5">
              {[1, 2, 3, 4, 5].map((n) => (
                <div key={n} className="space-y-1.5">
                  <Label htmlFor={`reform_${n}`} className="text-sm font-medium text-[#0B1220]">
                    Reform #{n} <span className="text-[#B42318]">*</span>
                  </Label>
                  <Textarea
                    id={`reform_${n}`}
                    rows={2}
                    required
                    minLength={3}
                    value={form[`reform_${n}`]}
                    onChange={update(`reform_${n}`)}
                    data-testid={`dialogue-reform-${n}`}
                    placeholder={`e.g. Governance change or system fix #${n}…`}
                  />
                </div>
              ))}

              <div className="space-y-1.5">
                <Label htmlFor="dl-additional" className="text-sm font-medium text-[#0B1220]">
                  Anything else you want NGRP to hear? (optional)
                </Label>
                <Textarea
                  id="dl-additional"
                  rows={3}
                  value={form.additional_thoughts}
                  onChange={update('additional_thoughts')}
                  data-testid="dialogue-additional"
                />
              </div>

              <label className="flex items-start gap-3 rounded-lg border border-[#D7E1EF] bg-[#F5F8FC] p-4 cursor-pointer">
                <Checkbox
                  checked={form.consent_share}
                  onCheckedChange={(v) => setForm((s) => ({ ...s, consent_share: !!v }))}
                  data-testid="dialogue-consent"
                />
                <span className="text-sm text-[#0B1220]">
                  You may share my reform ideas anonymously in the Maharashtra People’s Governance Report and NGRP policy publications.
                </span>
              </label>

              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#062B5C] text-white hover:bg-[#05244d]"
                data-testid="dialogue-form-submit-button"
              >
                {submitting ? t('common.submitting') : 'Submit your 5 reforms'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>

              {success && (
                <Card className="border-[#138A4B]/40 bg-[#EAF6EF]" data-testid="dialogue-success">
                  <CardContent className="p-5 flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[#138A4B]" />
                    <div>
                      <div className="font-semibold text-[#0B1220]">{t('common.thank_you')}</div>
                      <div className="text-sm text-[#0B1220]">{success.message}</div>
                      <div className="text-xs text-[#5B6B82] mt-1">Reference: {success.id}</div>
                      <div className="mt-4 flex flex-wrap gap-2">
                        <Link to="/join">
                          <Button size="sm" className="bg-[#062B5C] text-white hover:bg-[#05244d]">Now join NGRP</Button>
                        </Link>
                        <Link to="/aspirant">
                          <Button size="sm" variant="outline" className="border-[#D7E1EF] text-[#062B5C]">Apply as an aspirant</Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </form>
        </Container>
      </section>

      {/* Closing motto */}
      <section className="py-14 bg-[#F5F8FC]">
        <Container>
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#D7E1EF] bg-white px-3 py-1.5 text-xs font-medium text-[#062B5C]">
              <Sparkles className="h-3.5 w-3.5" /> Ideas first. Then organisation. Then leadership.
            </div>
            <p className="mt-4 font-display text-2xl sm:text-3xl font-semibold text-[#0B1220]">
              Knowledge → Public Service → Performance → Public Trust → Leadership
            </p>
          </div>
        </Container>
      </section>

      <TricolourRibbon />
    </div>
  );
}

function Field({ id, label, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-sm font-medium text-[#0B1220]">{label}</Label>
      {children}
    </div>
  );
}
